import React, { useCallback, useMemo, useState } from 'react';
import { AppButton, Box, Enrollment, FormProvider, Preloader, ShimmerBox } from '@common-fe/common-fe';

import { ModalWrapper } from '@/components/index.ts';
import useEmployerTerminationBanner from '@/modules/core/hooks/useEmployerTerminationBanner';
import { PolicyStatus } from '@/modules/employee/EnrollmentDetails/queries/useGetPolicyStatuses.query';

import { useGetEmployeeById } from '../hooks';

import { useDefaultEnrollmentFormValues } from './hooks/useDefaultEnrollmentFormValues';
import { useDynamicEnrollmentForm } from './hooks/useDynamicEnrollmentForm';
import { useEnrollmentModalActions } from './hooks/useEnrollmentModalActions';
import useEnrollmentSubmit from './hooks/useEnrollmentSubmit';
import { useGetEnrollmentSetupData } from './hooks/useGetEnrollmentSetupData';
import { useGetHealthPlanEnrollmentSetupData } from './hooks/useGetHealthPlanEnrollmentSetupData';
import { useModalVisibility } from './hooks/useModalVisibility';
import { useCreateHealthPlanEnrollmentQuery } from './queries/useCreateHealthPlanEnrollment.query';
import { useCreateEnrollment } from './queries/useCreateNewEnrollment.query';
import { getEmployeeAge, isPlanInPlans } from './utils/enrollmentUtils';
import EnrollmentErrorModal from './EnrollmentErrorModal';
import EnrollmentInfoBox from './EnrollmentInfoBox';
import { PlanTypes } from './enrollmentModal.types';
import EnrollmentModalActions from './EnrollmentModalActions';
import EnrollmentSection from './EnrollmentSection';
import IdentifiersSection from './IdentifiersSection';

interface EnrollmentModalProps {
  isVisible?: boolean;
  onSetVisible?: (value: boolean) => void;
  isEdit?: boolean;
  enrollment?: Enrollment;
  planType?: PlanTypes;
  healthPlanId?: string;
  statuses?: PolicyStatus[];
  lastCoverageEndDate?: string | null;
  coverageLevel?: string;
  hasSubmitClaimButton?: boolean;
  isWithoutButton?: boolean;
  children?: React.ReactNode;
}

const EnrollmentModal: React.FC<EnrollmentModalProps> = ({
  isVisible,
  onSetVisible,
  enrollment,
  isEdit,
  planType,
  healthPlanId,
  statuses,
  lastCoverageEndDate,
  coverageLevel,
  hasSubmitClaimButton,
  isWithoutButton,
  children,
}) => {
  const [minElectionAmountState, setMinElectionAmountState] = useState<number | undefined>(undefined);
  const [maxElectionAmountState, setMaxElectionAmountState] = useState<number | undefined>(undefined);
  const [selectedPlanId, setSelectedPlanId] = useState<string | undefined>(enrollment?.planId);
  const [selectedHealthPlanId, setSelectedHealthPlanId] = useState<string | undefined>(healthPlanId);
  const [errorModal, setErrorModal] = useState(false);
  const [conflictMessage, setConflictMessage] = useState<string | undefined>(undefined);

  const { status } = useEmployerTerminationBanner();

  const { currentModalVisible, currentSetModalVisible, prevModalVisibleRef } = useModalVisibility({
    isWithoutButton,
    isVisible,
    onSetVisible,
  });

  const handleElectionAmountChange = useCallback((min: number | undefined, max: number | undefined) => {
    setMinElectionAmountState(min);
    setMaxElectionAmountState(max);
  }, []);

  const { data: employeeData } = useGetEmployeeById();
  const employeeAge = useMemo(() => getEmployeeAge(employeeData?.birthDate), [employeeData?.birthDate]);

  const {
    healthPlanLoading,
    currentHealthPlan,
    healthPlansOptions,
    healthPlanCoverageOptions,
    isAddingHealthPlanEnrollmentAvailable,
  } = useGetHealthPlanEnrollmentSetupData({
    currentHealthPlanId: selectedHealthPlanId,
    isEdit,
  });

  const {
    SSN,
    EEID,
    PEID,
    lastName,
    employerName,
    partnerName,
    isLoading,
    plansOptions,
    currentPlan,
    planLoading,
    isAddingEnrollmentAvailable,
    isAllPlansHasPolicyRunout,
  } = useGetEnrollmentSetupData({
    currentPlanId: selectedPlanId,
    isEdit,
  });

  const defaultFormValues = useDefaultEnrollmentFormValues({
    partnerName,
    employerName,
    EEID,
    PEID,
    SSN,
    lastName,
    planType,
    isCurrentPlanInPlans: isPlanInPlans(enrollment?.planId, plansOptions),
    isEdit,
    enrollment,
    selectedPlanId,
    healthPlanId,
    selectedHealthPlanId,
    currentPlan,
    statuses,
    currentHealthPlan,
    coverageLevel,
    plansOptions,
    healthPlansOptions,
  });

  const validationParams = useMemo(
    () => ({
      minElectionAmount: minElectionAmountState,
      maxElectionAmount: maxElectionAmountState,
      lastCoverageEndDate: lastCoverageEndDate,
      planStartDate: currentPlan.planStartDate,
      planEndDate: currentPlan.planEndDate,
      catchUpAge: currentPlan?.catchUpAge,
      employeeAge: employeeAge,
      catchUpElection: currentPlan?.catchUpElection,
      healthPlanStartDate: currentHealthPlan.planStartDate,
      healthPlanEndDate: currentHealthPlan.planEndDate,
      contributionMonitoringRequired: currentPlan?.contributionMonitoringRequired,
    }),
    [
      currentHealthPlan.planEndDate,
      currentHealthPlan.planStartDate,
      currentPlan?.catchUpAge,
      currentPlan?.catchUpElection,
      currentPlan.planEndDate,
      currentPlan.planStartDate,
      currentPlan?.contributionMonitoringRequired,
      employeeAge,
      lastCoverageEndDate,
      maxElectionAmountState,
      minElectionAmountState,
    ]
  );

  const { methods, formType } = useDynamicEnrollmentForm({
    defaultFormValues,
    validationParams,
  });

  const handleConflictClear = useCallback(() => {
    setConflictMessage(undefined);
  }, []);

  const { handleCancel, handleTryAgain } = useEnrollmentModalActions({
    currentModalVisible,
    prevModalVisibleRef,
    methods,
    defaultFormValues,
    setErrorModal,
    currentSetModalVisible,
    setSelectedPlanId,
    setSelectedHealthPlanId,
    clearConflictMessage: handleConflictClear,
  });

  const currentTitle = useMemo(() => {
    const title = isEdit ? 'Edit Enrollment' : 'Add Enrollment';
    return `${title} for ${employeeData?.fullName || ''}`;
  }, [employeeData?.fullName, isEdit]);

  const { onSave, isLoading: onSaveLoading } = useCreateEnrollment(currentPlan?.luGpDesignationAllowed);
  const { onSave: onSaveHealthPlanEnrollment, isLoading: onSaveHealthPlanLoading } =
    useCreateHealthPlanEnrollmentQuery();

  const handleConflict = useCallback((message: string) => {
    setConflictMessage(message);
  }, []);

  const submitEnrollment = useEnrollmentSubmit({
    planType: planType || PlanTypes.HEALTH,
    healthPlansOptions,
    plansOptions,
    onSave,
    onSaveHealthPlanEnrollment,
    isEdit: !!isEdit,
    currentSetModalVisible,
    methods,
    setErrorModal,
    contributionMonitoringRequired: currentPlan?.contributionMonitoringRequired,
    onConflict: handleConflict,
  });

  const handleSubmit = useCallback(
    (formValues: Parameters<typeof submitEnrollment>[0]) => {
      setConflictMessage(undefined);
      return submitEnrollment(formValues);
    },
    [submitEnrollment]
  );

  return (
    <>
      <EnrollmentModalActions
        isEdit={!!isEdit}
        isWithoutButton={isWithoutButton}
        onSetVisible={currentSetModalVisible}
        isAddingEnrollmentAvailable={isAddingEnrollmentAvailable}
        isAddingHealthPlanEnrollmentAvailable={isAddingHealthPlanEnrollmentAvailable}
        isLoading={isLoading}
        status={status || ''}
        isAllPlansHasPolicyRunout={isAllPlansHasPolicyRunout}
        hasSubmitClaimButton={hasSubmitClaimButton}
      >
        {children}
      </EnrollmentModalActions>
      <ModalWrapper
        testId="enrollment-modal"
        visible={currentModalVisible}
        onSetVisible={currentSetModalVisible}
        title={currentTitle}
        isClosingByClickOutsideBlocked
        data-testid="enrollment-modal-wrapper"
      >
        <EnrollmentErrorModal
          visible={errorModal}
          onCancel={handleCancel}
          onTryAgain={handleTryAgain}
          data-testid="enrollment-error-modal"
        />
        <Box background="module" pad="l" round="moduleRound" data-testid="enrollment-modal-content">
          {isLoading ? (
            <Preloader data-testid="enrollment-modal-preloader" />
          ) : (
            <>
              <FormProvider key={formType} {...methods}>
                <Box gap="l" data-testid="enrollment-modal-form-section">
                  <IdentifiersSection />
                  {onSaveLoading || onSaveHealthPlanLoading ? (
                    <ShimmerBox width="100%" height="775px" />
                  ) : (
                    <EnrollmentSection
                      plansOptions={plansOptions}
                      planType={planType}
                      employeeAge={employeeAge}
                      currentPlan={currentPlan}
                      healthPlansOptions={healthPlansOptions}
                      healthPlanLoading={healthPlanLoading}
                      planLoading={planLoading}
                      isEdit={isEdit}
                      healthPlanCoverageLevel={coverageLevel}
                      healthPlanCoverageOptions={healthPlanCoverageOptions}
                      conflictMessage={conflictMessage}
                      onElectionAmountChange={handleElectionAmountChange}
                      onPlanChange={setSelectedPlanId}
                      onHealthPlanChange={setSelectedHealthPlanId}
                      onConflictClear={handleConflictClear}
                      data-testid="enrollment-section"
                    />
                  )}
                </Box>
              </FormProvider>
              {!isEdit && <EnrollmentInfoBox data-testid="enrollment-info-box" />}
            </>
          )}
        </Box>
        <Box
          direction="row"
          justify="end"
          gap="m"
          margin={{ top: 'l' }}
          data-testid="enrollment-modal-actions"
        >
          <AppButton
            type="button"
            width="140px"
            buttonType="secondary"
            onClick={() => currentSetModalVisible(false)}
            testId="enrollment-cancel"
          >
            Cancel
          </AppButton>
          <AppButton
            width="140px"
            disabled={isLoading || onSaveLoading || onSaveHealthPlanLoading}
            onClick={methods.handleSubmit(handleSubmit)}
            testId="enrollment-submit"
          >
            {isLoading || onSaveLoading || onSaveHealthPlanLoading ? <Preloader color="white" /> : 'Submit'}
          </AppButton>
        </Box>
      </ModalWrapper>
    </>
  );
};

export default EnrollmentModal;
